import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Settings {
  wordLength: number;
  useUmlauts: boolean;
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<Settings>({ wordLength: 5, useUmlauts: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/games/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      const response = await fetch(`${API_URL}/games/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        setMessage('Einstellungen gespeichert!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Fehler beim Speichern');
      }
    } catch (err) {
      setMessage('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-white text-xl">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-4">
      <div className="max-w-2xl mx-auto pt-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">Einstellungen</h1>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Wortlänge
              </label>
              <div className="grid grid-cols-2 gap-4">
                {[3, 4, 5, 6].map((length) => (
                  <button
                    key={length}
                    onClick={() => setSettings({ ...settings, wordLength: length })}
                    className={`py-3 px-4 rounded-lg font-semibold transition-colors ${
                      settings.wordLength === length
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                  >
                    {length} Buchstaben
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Umlaute
              </label>
              <div className="flex gap-4">
                <button
                  onClick={() => setSettings({ ...settings, useUmlauts: true })}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors ${
                    settings.useUmlauts
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Mit Umlauten (ä, ö, ü, ß)
                </button>
                <button
                  onClick={() => setSettings({ ...settings, useUmlauts: false })}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors ${
                    !settings.useUmlauts
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Ohne Umlaute
                </button>
              </div>
            </div>

            {message && (
              <div className={`px-4 py-3 rounded-lg ${
                message.includes('gespeichert') 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {message}
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <button
                onClick={saveSettings}
                disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50"
              >
                {saving ? 'Speichert...' : 'Speichern'}
              </button>
              <button
                onClick={() => navigate('/menu')}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                Zurück
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;

